import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";

/** Корень вкладки: своя прокрутка, как у вкладок «Administration». */
export const tabRoot = css`
  height: 100%;
  overflow-y: auto;
  padding: 15px;
`;

/** Таблица со своей горизонтальной прокруткой — страница вбок не едет. */
export const tableScroll = css`
  overflow-x: auto;
  min-width: 0;
`;

/** Списки только для чтения: строка открывает панель. */
export const list = (theme: ExactTheme) => css`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  background: ${theme.colorsV2.white};
  th,
  td {
    text-align: left;
    padding: 10px 12px;
    border-bottom: 1px solid ${theme.colorsV2.grey[100]};
    vertical-align: middle;
  }
  th {
    font-weight: 500;
    color: ${theme.colorsV2.grey[800]};
    white-space: nowrap;
  }
`;

export const rowCss = (selected: boolean) => (theme: ExactTheme) => css`
  cursor: pointer;
  // Строка-карточка на узком экране закрашивается целиком, без щелей между ячейками.
  background: ${selected ? theme.colorsV2.blue[25] : "transparent"};
  td {
    background: ${selected ? theme.colorsV2.blue[25] : "transparent"};
  }
  &:hover td {
    background: ${selected ? theme.colorsV2.blue[25] : theme.colorsV2.grey[25]};
  }
  &:focus-visible {
    outline: 2px solid ${theme.colorsV2.blue[500]};
    outline-offset: -2px;
  }
`;

export const numeric = css`
  text-align: right !important;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

/** Вторая строка ячейки: мельче, но с контрастом не ниже AA. */
export const subline = (theme: ExactTheme) => css`
  font-size: 12px;
  color: ${theme.colorsV2.grey[800]};
  margin-top: 2px;
`;

export const muted = (theme: ExactTheme) => css`
  color: ${theme.colorsV2.grey[800]};
`;

export const money =
  (tone: "ok" | "warning" | "error") => (theme: ExactTheme) => css`
    color: ${tone === "ok"
      ? "inherit"
      : tone === "warning"
      ? theme.colorsV2.orange[800]
      : theme.colorsV2.red[700]};
    font-weight: ${tone === "ok" ? 400 : 600};
  `;

/**
 * Список и панель рядом; уже 1200px — панель под списком (иначе таблица
 * заезжает под неё). На 1280 рядом ещё помещаются.
 */
export const withPanelLayout = (open: boolean) => css`
  display: grid;
  // На 1200 панель 360px, чтобы таблица рядом не прокручивалась вбок.
  grid-template-columns: ${open
    ? "minmax(0, 1fr) clamp(360px, 30vw, 400px)"
    : "minmax(0, 1fr)"};
  gap: 16px;
  align-items: start;
  @media (max-width: 1199px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const toolbar = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding-bottom: 12px;
`;

/**
 * Обёртка общего SegmentedControl: подписи в одну строку с отступами,
 * на узком экране переключатель прокручивается сам.
 */
export const segmentScroll = css`
  overflow-x: auto;
  max-width: 100%;
  & > div {
    min-width: max-content;
  }
  & > div > button {
    padding: 0 10px;
    white-space: nowrap;
  }
`;

/** Поле поиска над списком. */
export const searchField = css`
  width: 260px;
  max-width: 100%;
`;

export const errorBox = css`
  padding-bottom: 12px;
`;

export const nowrap = css`
  white-space: nowrap;
`;

/**
 * Строка панели: подпись слева, кнопка справа. Узко — кнопка уходит под
 * подпись, а не за край панели.
 */
export const lineRow = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

export const grow = css`
  flex: 1 1 160px;
  min-width: 0;
`;

/** Кнопка в шапке карточки не сжимается до «B…» на узком экране. */
export const noShrink = css`
  flex-shrink: 0;
`;

// Пока идёт обновление, список приглушён: видно, что данные сейчас сменятся.
export const refreshingList = css`
  opacity: 0.45;
  pointer-events: none;
  transition: opacity 0.15s;
`;
